import React from 'react';
import { Link } from 'react-router-dom';
import { expertService, sessionService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Video, Calendar, Star, Clock, CheckCircle2, AlertCircle, ArrowRight, Users, History } from 'lucide-react';
import { ActivityHistory } from '../components/shared/ActivityHistory';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-cream-200 rounded-2xl p-5 flex flex-col gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon size={18} /></div>
    <div>
      <div className="font-serif text-2xl font-bold text-forest-900">{value ?? '—'}</div>
      <div className="text-xs text-forest-500 mt-0.5">{label}</div>
    </div>
  </div>
);

const BookingCard = ({ booking, onRespond }) => {
  // ✅ FIX: backend returns clientProfile not client
  const clientName = `${booking.clientProfile?.user?.firstName ?? ''} ${booking.clientProfile?.user?.lastName ?? ''}`.trim();
  const date = new Date(booking.scheduledAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' });
  const isPending = booking.status === 'PENDING';

  return (
    <div className="bg-white border border-cream-200 rounded-2xl p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0">
          {booking.clientProfile?.user?.firstName?.[0]}{booking.clientProfile?.user?.lastName?.[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h4 className="font-sans font-semibold text-forest-900 text-sm">{clientName || 'Client'}</h4>
              <p className="text-xs text-forest-500 mt-0.5">{booking.sessionType} · {booking.duration} mins · {date}</p>
              {booking.level && <p className="text-xs text-forest-400 mt-0.5">{booking.level.name}</p>}
            </div>
            <span className={`text-[10px] font-medium border px-2.5 py-1 rounded-full flex-shrink-0 ${isPending ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-forest-50 text-forest-700 border-forest-200'}`}>
              {booking.status}
            </span>
          </div>
          {booking.notes && <p className="text-xs text-forest-400 mt-2 italic line-clamp-2">"{booking.notes}"</p>}
        </div>
      </div>
      {isPending && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100">
          <button onClick={() => onRespond(booking.id, 'confirm')} className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
            <CheckCircle2 size={13} /> Confirm
          </button>
          <button onClick={() => onRespond(booking.id, 'cancel')} className="text-xs px-4 py-1.5 rounded-full border border-rust-200 text-rust-500 hover:bg-rust-500 hover:text-white transition-all font-medium">
            Decline
          </button>
        </div>
      )}
      {booking.status === 'CONFIRMED' && booking.session?.id && (
        <div className="mt-4 pt-4 border-t border-cream-100">
          <Link to={`/sessions/${booking.session.id}`} className="btn-primary text-xs py-1.5 px-4">Start Session →</Link>
        </div>
      )}
    </div>
  );
};

const ExpertDashboardPage = () => {
  const { data: dashData, loading: dashLoading, error } = useApi(() => expertService.getMyDashboard());
  const { data: bookingsData, loading: bookingsLoading, refetch } = useApi(
    () => expertService.getMyBookings({ status: 'PENDING', page: 1, limit: 5 }),
  );
  const { data: upcomingData } = useApi(
    () => expertService.getMyBookings({ status: 'CONFIRMED', page: 1, limit: 3 }),
  );
  // History data — all bookings
  const { data: allBookingsData } = useApi(() => expertService.getMyBookings({ page: 1, limit: 100 }));

  const stats = dashData?.stats ?? {};

  const allBookings = Array.isArray(allBookingsData?.bookings) ? allBookingsData.bookings
    : Array.isArray(allBookingsData?.data) ? allBookingsData.data
    : Array.isArray(allBookingsData) ? allBookingsData : [];

  // ✅ FIX: consistent array extraction for both lists
  const pendingBookings = Array.isArray(bookingsData) ? bookingsData
    : Array.isArray(bookingsData?.bookings) ? bookingsData.bookings
    : Array.isArray(bookingsData?.data) ? bookingsData.data : [];

  const upcomingBookings = Array.isArray(upcomingData) ? upcomingData
    : Array.isArray(upcomingData?.bookings) ? upcomingData.bookings
    : Array.isArray(upcomingData?.data) ? upcomingData.data : [];

  const handleRespond = async (bookingId, action) => {
    try {
      await expertService.respondToBooking(bookingId, action);
      toast.success(action === 'confirm' ? 'Booking confirmed! Client has been notified.' : 'Booking declined.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  return (
    <DashboardLayout title="Expert Dashboard">
      <div className="space-y-8 max-w-4xl">

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span className="text-sm">Could not load dashboard — make sure your API server is running.</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Calendar}    label="Pending Bookings"  value={dashLoading ? '...' : stats.pendingBookings}   color="text-amber-600 bg-amber-50" />
          <StatCard icon={CheckCircle2} label="Confirmed"        value={dashLoading ? '...' : stats.confirmedBookings} color="text-forest-600 bg-forest-50" />
          <StatCard icon={Video}       label="Sessions Done"     value={dashLoading ? '...' : stats.completedSessions} color="text-blue-600 bg-blue-50" />
          <StatCard icon={Star}        label="Avg. Rating"       value={dashLoading ? '...' : stats.averageRating ? Number(stats.averageRating).toFixed(1) : '—'} color="text-gold-500 bg-amber-50" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg text-forest-900">Pending Requests</h3>
            <Link to="/expert/bookings" className="text-xs text-forest-500 hover:text-forest-800 flex items-center gap-1 font-medium">
              View all <ArrowRight size={13} />
            </Link>
          </div>
          {bookingsLoading ? (
            <div className="space-y-3">{[1,2].map((i) => <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse h-24" />)}</div>
          ) : pendingBookings.length === 0 ? (
            <div className="bg-white border border-cream-200 rounded-2xl p-8 text-center text-forest-400">
              <Calendar size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No pending booking requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingBookings.map((b) => <BookingCard key={b.id} booking={b} onRespond={handleRespond} />)}
            </div>
          )}
        </div>

        {upcomingBookings.length > 0 && (
          <div>
            <h3 className="font-serif text-lg text-forest-900 mb-4">Upcoming Sessions</h3>
            <div className="space-y-3">
              {upcomingBookings.map((b) => {
                // ✅ FIX: clientProfile not client
                const clientName = `${b.clientProfile?.user?.firstName ?? ''} ${b.clientProfile?.user?.lastName ?? ''}`.trim();
                const date = new Date(b.scheduledAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' });
                return (
                  <div key={b.id} className="bg-white border border-cream-200 border-l-4 border-l-forest-600 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-9 h-9 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Video size={16} className="text-forest-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-forest-900">{b.sessionType} with {clientName || 'Client'}</div>
                      <div className="text-xs text-forest-500 mt-0.5">{date} · {b.duration} mins</div>
                    </div>
                    {b.session?.id ? (
                      <Link to={`/sessions/${b.session.id}`} className="btn-primary text-xs py-1.5 px-4 flex-shrink-0">Open →</Link>
                    ) : (
                      <Link to="/expert/bookings" className="text-xs text-forest-400 flex-shrink-0">Details →</Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: 'Update Availability', to: '/settings',          icon: Clock },
            { label: 'View My Profile',     to: '/settings',          icon: Users },
            { label: 'Grade Submissions',   to: '/expert/submissions', icon: Star },
          ].map((item) => (
            <Link key={item.label} to={item.to}
              className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-sm hover:border-forest-200 transition-all group">
              <div className="w-9 h-9 bg-forest-50 rounded-xl flex items-center justify-center group-hover:bg-forest-100 transition-colors">
                <item.icon size={16} className="text-forest-600" />
              </div>
              <span className="text-sm font-medium text-forest-700">{item.label}</span>
            </Link>
          ))}
        </div>

        {/* ── Session History & Activity ── */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <History size={20} className="text-forest-600" />
            <h3 className="font-serif text-xl font-semibold text-forest-900">Session History & Activity</h3>
          </div>
          <ActivityHistory
            bookings={allBookings}
            canvasItems={[]}
            role="EXPERT"
          />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default ExpertDashboardPage;